import React, { RefObject } from "react";
import { IconX } from "@tabler/icons-react";
import VoterPhotoCameraStep from "./voter-photo-camera-step";
import VoterPhotoPreviewStep from "./voter-photo-preview-step";
import SrFaceMatchPanel from "./sr-face-match-panel";
import SrFaceCheckLoading from "./sr-face-check-loading";
import FaceSearchFeedback from "./face-search-feedback";
import type { FaceSearchError } from "./face-search-errors";
import type { SrFaceCheckResult, SrFaceDecision } from "./sr-face-check-types";

type Props = {
    title: string;
    subtitle: string;
    isBusy: boolean;
    capturedImage: string | null;
    faceCheck: SrFaceCheckResult | null;
    faceError: FaceSearchError | null;
    cameraRef: RefObject<any>;
    fileInputRef: RefObject<HTMLInputElement>;
    onClose: () => void;
    onCapture: () => void;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onRetake: () => void;
    onConfirm: () => void;
    onDecide: (voterid: string, decision: SrFaceDecision) => void;
    onSkipNoMatch: () => void;
};

const VoterPhotoModal = ({
    title,
    subtitle,
    isBusy,
    capturedImage,
    faceCheck,
    faceError,
    cameraRef,
    fileInputRef,
    onClose,
    onCapture,
    onFileChange,
    onRetake,
    onConfirm,
    onDecide,
    onSkipNoMatch,
}: Props) => {
    const showChecking = isBusy && !faceCheck && !faceError && Boolean(capturedImage);

    return (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !isBusy) onClose();
            }}
        >
            <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b px-5 py-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                        <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isBusy}
                        className="rounded-full p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                    >
                        <IconX size={20} />
                    </button>
                </div>

                <div className="p-5">
                    {showChecking ? (
                        <SrFaceCheckLoading />
                    ) : faceError ? (
                        <FaceSearchFeedback
                            isSearching={false}
                            error={faceError}
                            onRetry={onRetake}
                            onClose={onClose}
                        />
                    ) : faceCheck ? (
                        <SrFaceMatchPanel
                            check={faceCheck}
                            previewSrc={capturedImage}
                            busy={isBusy}
                            onDecide={onDecide}
                            onSkipNoMatch={onSkipNoMatch}
                        />
                    ) : !capturedImage ? (
                        <VoterPhotoCameraStep
                            cameraRef={cameraRef}
                            fileInputRef={fileInputRef}
                            onCapture={onCapture}
                            onFileChange={onFileChange}
                        />
                    ) : (
                        <VoterPhotoPreviewStep
                            imageSrc={capturedImage}
                            isBusy={isBusy}
                            busyLabel="Checking face..."
                            onRetake={onRetake}
                            onConfirm={onConfirm}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default VoterPhotoModal;
